import { supabase } from '../lib/supabase';
import { trackMeetingCreated } from '../utils/analytics';

// Helper function to check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
}

// Convert camelCase to snake_case
function toSnakeCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = typeof obj?.[key] === 'object' ? toSnakeCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
}

// Generate unique session code
function generateSessionCode() {
  return 'MEET' + Math.random()?.toString(36)?.substring(2, 8)?.toUpperCase();
}

// Create a new meeting
export async function createMeeting(meetingData) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const sessionCode = generateSessionCode();
    const snakeData = toSnakeCase({
      sessionCode,
      creatorId: user?.id,
      ...meetingData,
      status: 'pending'
    });

    const { data, error } = await supabase?.from('meetings')?.insert(snakeData)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Create meeting error:', error?.message);
      return null;
    }

    const result = toCamelCase(data);
    
    // Track meeting creation
    trackMeetingCreated({
      id: result?.id,
      participantCount: meetingData?.participantCount,
      meetingDate: result?.meetingDate,
      locationType: result?.locationType
    });

    return result;
  } catch (error) {
    console.error('Create meeting failed:', error?.message);
    throw error;
  }
}

// Get meeting by session code
export async function getMeetingBySessionCode(sessionCode) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const { data, error } = await supabase?.from('meetings')?.select('*')?.eq('session_code', sessionCode)?.maybeSingle();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') {
        return null;
      }
      console.log('Get meeting error:', error?.message);
      return null;
    }

    return data ? toCamelCase(data) : null;
  } catch (error) {
    console.error('Get meeting failed:', error?.message);
    throw error;
  }
}

// Get meeting by ID
export async function getMeetingById(meetingId) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const { data, error } = await supabase?.from('meetings')?.select('*')?.eq('id', meetingId)?.maybeSingle();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') {
        return null;
      }
      console.log('Get meeting error:', error?.message);
      return null;
    }

    return data ? toCamelCase(data) : null;
  } catch (error) {
    console.error('Get meeting failed:', error?.message);
    throw error;
  }
}

// Add participant to meeting
export async function addParticipant(participantData) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const snakeData = toSnakeCase({
      ...participantData,
      userId: user?.id,
      hasConfirmed: false
    });

    const { data, error } = await supabase?.from('meeting_participants')?.insert(snakeData)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Add participant error:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Add participant failed:', error?.message);
    throw error;
  }
}

// Get participants for a meeting
export async function getMeetingParticipants(meetingId) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const { data, error } = await supabase?.from('meeting_participants')?.select('*')?.eq('meeting_id', meetingId)?.order('created_at', { ascending: true });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') {
        return [];
      }
      console.log('Get participants error:', error?.message);
      return [];
    }

    return data ? data?.map(toCamelCase) : [];
  } catch (error) {
    console.error('Get participants failed:', error?.message);
    throw error;
  }
}

// Confirm participation
export async function confirmParticipation(meetingId) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const { data, error } = await supabase?.from('meeting_participants')?.update({
        has_confirmed: true,
        confirmed_at: new Date()?.toISOString()
      })?.eq('meeting_id', meetingId)?.eq('user_id', user?.id)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Confirm participation error:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Confirm participation failed:', error?.message);
    throw error;
  }
}

// Subscribe to meeting participants changes
export function subscribeToMeetingParticipants(meetingId, callback) {
  const channel = supabase?.channel(`meeting_participants_${meetingId}`)?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'meeting_participants',
        filter: `meeting_id=eq.${meetingId}`
      },
      (payload) => {
        callback(payload);
      }
    )?.subscribe();

  return () => {
    supabase?.removeChannel(channel);
  };
}

// Subscribe to meeting status changes
export function subscribeToMeetingStatus(meetingId, callback) {
  const channel = supabase?.channel(`meeting_status_${meetingId}`)?.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'meetings',
        filter: `id=eq.${meetingId}`
      },
      (payload) => {
        callback(payload);
      }
    )?.subscribe();

  return () => {
    supabase?.removeChannel(channel);
  };
}

// Update participant location
export async function updateParticipantLocation(meetingId, locationData) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const snakeData = toSnakeCase({
      meetingId,
      userId: user?.id,
      ...locationData
    });

    const { data, error } = await supabase?.from('participant_locations')?.upsert(snakeData, { onConflict: 'meeting_id,user_id' })?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Update location error:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Update location failed:', error?.message);
    throw error;
  }
}

// Get participant locations for a meeting
export async function getParticipantLocations(meetingId) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const { data, error } = await supabase?.from('participant_locations')?.select('*')?.eq('meeting_id', meetingId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') {
        return [];
      }
      console.log('Get locations error:', error?.message);
      return [];
    }

    return data ? data?.map(toCamelCase) : [];
  } catch (error) {
    console.error('Get locations failed:', error?.message);
    throw error;
  }
}

// Update participant status
export async function updateParticipantStatus(meetingId, status, message = '') {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const snakeData = toSnakeCase({
      meetingId,
      userId: user?.id,
      status,
      message
    });

    const { data, error } = await supabase?.from('participant_status')?.upsert(snakeData, { onConflict: 'meeting_id,user_id' })?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Update status error:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Update status failed:', error?.message);
    throw error;
  }
}

// Get participant statuses for a meeting
export async function getParticipantStatuses(meetingId) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const { data, error } = await supabase?.from('participant_status')?.select('*')?.eq('meeting_id', meetingId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') {
        return [];
      }
      console.log('Get statuses error:', error?.message);
      return [];
    }

    return data ? data?.map(toCamelCase) : [];
  } catch (error) {
    console.error('Get statuses failed:', error?.message);
    throw error;
  }
}

// Get user profile with emergency contact
export async function getUserProfile(userId) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.maybeSingle();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') {
        return null;
      }
      console.log('Get user profile error:', error?.message);
      return null;
    }

    return data ? toCamelCase(data) : null;
  } catch (error) {
    console.error('Get user profile failed:', error?.message);
    throw error;
  }
}

// Update user emergency contact
export async function updateEmergencyContact(emergencyContactName, emergencyContactPhone) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const { data, error } = await supabase?.from('user_profiles')?.update({
        emergency_contact_name: emergencyContactName,
        emergency_contact_phone: emergencyContactPhone
      })?.eq('id', user?.id)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Update emergency contact error:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Update emergency contact failed:', error?.message);
    throw error;
  }
}

// Subscribe to participant locations changes
export function subscribeToParticipantLocations(meetingId, callback) {
  const channel = supabase?.channel(`participant_locations_${meetingId}`)?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'participant_locations',
        filter: `meeting_id=eq.${meetingId}`
      },
      (payload) => {
        callback(payload);
      }
    )?.subscribe();

  return () => {
    supabase?.removeChannel(channel);
  };
}

// Subscribe to participant status changes
export function subscribeToParticipantStatuses(meetingId, callback) {
  const channel = supabase?.channel(`participant_statuses_${meetingId}`)?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'participant_status',
        filter: `meeting_id=eq.${meetingId}`
      },
      (payload) => {
        callback(payload);
      }
    )?.subscribe();

  return () => {
    supabase?.removeChannel(channel);
  };
}

// Create meeting invitation
export async function createInvitation(invitationData) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const invitationToken = 'INV' + Math.random()?.toString(36)?.substring(2, 22)?.toUpperCase();
    const snakeData = toSnakeCase({
      ...invitationData,
      inviterId: user?.id,
      invitationToken,
      status: 'pending'
    });

    const { data, error } = await supabase?.from('meeting_invitations')?.insert(snakeData)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Create invitation error:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Create invitation failed:', error?.message);
    throw error;
  }
}

// Send invitation via edge function
export async function sendInvitation(invitationId, sendEmail = true, sendSMS = false) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const { data, error } = await supabase?.functions?.invoke('send-meeting-invitation', {
      body: { invitationId, sendEmail, sendSMS }
    });

    if (error) {
      console.error('Send invitation error:', error?.message);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Send invitation failed:', error?.message);
    throw error;
  }
}

// Get invitations for a meeting
export async function getMeetingInvitations(meetingId) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const { data, error } = await supabase?.from('meeting_invitations')?.select('*')?.eq('meeting_id', meetingId)?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') {
        return [];
      }
      console.log('Get invitations error:', error?.message);
      return [];
    }

    return data ? data?.map(toCamelCase) : [];
  } catch (error) {
    console.error('Get invitations failed:', error?.message);
    throw error;
  }
}

// Get invitation by token (public access)
export async function getInvitationByToken(token) {
  try {
    const { data, error } = await supabase?.from('meeting_invitations')?.select(`
        *,
        meetings!meeting_invitations_meeting_id_fkey (
          id,
          location_name,
          location_address,
          meeting_date,
          meeting_time,
          session_code
        ),
        user_profiles!meeting_invitations_inviter_id_fkey (
          full_name
        )
      `)?.eq('invitation_token', token)?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Get invitation by token error:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Get invitation by token failed:', error?.message);
    throw error;
  }
}

// Accept invitation
export async function acceptInvitation(invitationId) {
  try {
    const { data, error } = await supabase?.from('meeting_invitations')?.update({
      status: 'accepted',
      accepted_at: new Date()?.toISOString()
    })?.eq('id', invitationId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Accept invitation error:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Accept invitation failed:', error?.message);
    throw error;
  }
}

// Subscribe to meeting invitations
export function subscribeToMeetingInvitations(meetingId, callback) {
  const channel = supabase?.channel(`meeting_invitations_${meetingId}`)?.on(
    'postgres_changes',
    {
      event: '*',
      schema: 'public',
      table: 'meeting_invitations',
      filter: `meeting_id=eq.${meetingId}`
    },
    callback
  )?.subscribe();

  return () => {
    supabase?.removeChannel(channel);
  };
}

// Get all meetings for the current user
export async function getMeetingsByUser(user) {
  try {
    if (!user) throw new Error('User not authenticated');

    // Query meetings where user is creator OR participant
    // Use two separate queries and merge results to avoid PostgREST OR syntax issues
    const [creatorMeetings, participantMeetings] = await Promise.all([
      // Meetings where user is creator
      supabase?.from('meetings')
        ?.select('*')
        ?.eq('creator_id', user?.id)
        ?.order('created_at', { ascending: false }),
      
      // Meetings where user is participant
      supabase?.from('meetings')
        ?.select(`
          *,
          meeting_participants!inner(
            user_id
          )
        `)
        ?.eq('meeting_participants.user_id', user?.id)
        ?.order('created_at', { ascending: false })
    ]);

    if (creatorMeetings?.error) {
      if (isSchemaError(creatorMeetings?.error)) {
        console.error('Schema error (creator meetings):', creatorMeetings?.error?.message);
        throw creatorMeetings?.error;
      }
      console.log('Get creator meetings error:', creatorMeetings?.error?.message);
    }

    if (participantMeetings?.error) {
      if (isSchemaError(participantMeetings?.error)) {
        console.error('Schema error (participant meetings):', participantMeetings?.error?.message);
        throw participantMeetings?.error;
      }
      console.log('Get participant meetings error:', participantMeetings?.error?.message);
    }

    // Merge and deduplicate results
    const allMeetings = [
      ...(creatorMeetings?.data || []),
      ...(participantMeetings?.data || [])
    ];

    // Remove duplicates by meeting ID
    const uniqueMeetings = Array.from(
      new Map(allMeetings.map(meeting => [meeting.id, meeting]))?.values()
    );

    // Sort by created_at descending
    uniqueMeetings?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return uniqueMeetings ? uniqueMeetings?.map(meeting => toCamelCase(meeting)) : [];
  } catch (error) {
    console.error('Get meetings by user failed:', error?.message);
    throw error;
  }
}
